// product/PurchaseDrawer.jsx
// Slide-up drawer containing the buyer form.
// Fields: name, whatsapp, address (mandatory), district (mandatory),
//         map (optional — encouraged), notes (optional).
// Delivery banner: "Entrega hoy" before 3 PM, "Entrega mañana" after 3 PM.
// Submits to POST /api/entrega with seller_id as user_id (hidden from buyer).
// On success → calls onSuccess({ deliveryPointId })

import React, { useState, useEffect } from "react";
import { X, ShoppingBag, ChevronRight, AlertCircle, Clock, Truck, CalendarClock } from "lucide-react";
import { brand } from "../shared/brand";
import { AddressMap } from "./AddressMap";

const API_URL = process.env.REACT_APP_API_URL;

// ── Delivery timing helpers ───────────────────────────────────────────────────

const CUTOFF_HOUR = 15; // 3:00 PM Peru time

function getDeliveryInfo() {
  const nowPeru  = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Lima' }));
  const hour     = nowPeru.getHours();
  const isToday  = hour < CUTOFF_HOUR;

  return {
    isToday,
    label: isToday ? "Entrega hoy" : "Entrega mañana",
    color: isToday ? brand.accentGreen : brand.accentGold,
  };
}

// ── Delivery Banner ───────────────────────────────────────────────────────────

function DeliveryBanner() {
  const { isToday, label, color } = getDeliveryInfo();

  return (
    <div
      className="rounded-2xl p-3.5 flex flex-col gap-2"
      style={{ background: color + "14", border: `1px solid ${color}40` }}
    >
      {/* Top row: chip + schedule */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {/* Chip */}
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-extrabold text-white"
          style={{ background: color }}
        >
          <Truck className="size-3" />
          {label}
        </span>

        {/* Schedule */}
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500">
          <Clock className="size-3.5 shrink-0" />
          Entregas: 11:00 AM – 8:00 PM
        </span>
      </div>

      {/* Time slot reminder */}
      <div className="flex items-start gap-2">
        <CalendarClock className="size-3.5 shrink-0 mt-0.5" style={{ color }} />
        <p className="text-xs text-slate-600 leading-relaxed">
          Elige tu horario preferido usando el enlace que te enviamos por WhatsApp.
          {!isToday && (
            <span className="block mt-0.5 text-slate-400">
              Como es después de las 3 PM, tu pedido se programará para mañana.
            </span>
          )}
        </p>
      </div>
    </div>
  );
}

// ── Main Drawer ───────────────────────────────────────────────────────────────

export function PurchaseDrawer({ product, open, onClose, onSuccess, googleMapsLoaded }) {
  const [form, setForm] = useState({ name: "", phone: "", notes: "" });

  const [location, setLocation] = useState({
    address:  "",
    district: "",
    lat:      null,
    lng:      null,
  });

  // Track whether user has interacted with the map at all
  const [mapAttempted, setMapAttempted] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [errors,     setErrors]     = useState({});

  // Lock body scroll while drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Reset form when drawer opens for a new product
  useEffect(() => {
    if (open) {
      setForm({ name: "", phone: "", notes: "" });
      setLocation({ address: "", district: "", lat: null, lng: null });
      setMapAttempted(false);
      setErrors({});
    }
  }, [open, product?.id]);

  function set(field, val) {
    setForm(f => ({ ...f, [field]: val }));
    setErrors(e => ({ ...e, [field]: "" }));
  }

  function handleLocationChange(loc) {
    setLocation(loc);
    setMapAttempted(true); // user interacted with map or search
    setErrors(e => ({ ...e, address: "", district: "" }));
  }

  // ── Validation ─────────────────────────────────────────────────────────────
  // address text  → mandatory
  // district      → mandatory
  // lat/lng       → optional (encouraged via map, but not blocking)
  function validate() {
    const e = {};
    if (!form.name.trim())
      e.name = "Ingresa tu nombre completo.";
    if (!/^\d{9}$/.test(form.phone.replace(/\s/g, "")))
      e.phone = "Ingresa un número WhatsApp de 9 dígitos.";
    if (!location.address.trim())
      e.address = "Ingresa tu dirección de entrega.";
    if (!location.district)
      e.district = "Selecciona tu distrito.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  async function handleSubmit() {
    if (!validate() || !product) return;
    setSubmitting(true);

    const payload = {
      user_id:          product.seller_id,
      recipient_name:   form.name.trim(),
      recipient_phone:  form.phone.replace(/\s/g, ""),
      additional_notes: form.notes.trim(),
      address:          location.address,
      district:         location.district,
      // lat/lng sent only if captured — backend accepts null
      provisional_latitude:  location.lat  ?? null,
      provisional_longitude: location.lng  ?? null,
      items:  [{ sku: product.sku, quantity: 1 }],
      charge: parseFloat(product.price),
    };

    try {
      const res  = await fetch(`${API_URL}/api/entrega`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        setErrors({ submit: data.error || "Ocurrió un error. Intenta de nuevo." });
        return;
      }
      onSuccess({ deliveryPointId: data.delivery_point_id });
    } catch {
      setErrors({ submit: "Sin conexión. Verifica tu internet e intenta de nuevo." });
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 flex flex-col rounded-t-3xl bg-white shadow-2xl transition-transform duration-300 ease-out ${
          open ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ maxHeight: "92dvh" }}
      >
        {/* Handle + header */}
        <div className="shrink-0 px-4 pt-3 pb-4 border-b border-slate-100">
          <div className="mx-auto w-10 h-1 rounded-full bg-slate-200 mb-4" />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">
                Comprando
              </p>
              <h2 className="text-base font-extrabold text-slate-900 leading-tight line-clamp-1">
                {product?.title}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-lg font-extrabold" style={{ color: brand.primary }}>
                S/ {product ? parseFloat(product.price).toFixed(2) : "—"}
              </span>
              <button
                onClick={onClose}
                className="grid size-8 place-items-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable form body */}
        <div className="flex-1 overflow-y-auto px-4 py-5 space-y-5">

          {/* ── Delivery banner — top of form ── */}
          <DeliveryBanner />

          {/* Name */}
          <FormField label="Nombre completo" error={errors.name} required>
            <input
              type="text"
              value={form.name}
              onChange={e => set("name", e.target.value)}
              placeholder="Ej: María García"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-slate-400"
            />
          </FormField>

          {/* WhatsApp */}
          <FormField label="WhatsApp" error={errors.phone} required>
            <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50 overflow-hidden focus-within:border-slate-400">
              <span className="shrink-0 px-3 text-sm text-slate-400 border-r border-slate-200 py-2.5 bg-white">
                +51
              </span>
              <input
                type="tel"
                inputMode="numeric"
                value={form.phone}
                onChange={e => set("phone", e.target.value.replace(/\D/g, "").slice(0, 9))}
                placeholder="999 999 999"
                className="flex-1 px-3 py-2.5 text-sm bg-transparent outline-none"
              />
            </div>
          </FormField>

          {/* Address — text input mandatory, map encouraged but optional */}
          <FormField
            label="Dirección de entrega"
            error={errors.address || errors.district}
            required
            hint="Usa el mapa para ayudarnos a encontrarte más rápido, pero si no puedes escribe tu dirección completa abajo."
          >
            <AddressMap
              onLocationChange={handleLocationChange}
              googleMapsLoaded={googleMapsLoaded}
            />
          </FormField>

          {/* Nudge if user hasn't touched the map yet and address text is already typed */}
          {!mapAttempted && location.address.trim().length > 5 && (
            <div
              className="flex items-start gap-2 rounded-xl px-3 py-2.5 text-xs text-slate-600"
              style={{ background: brand.accentGold + "18", border: `1px solid ${brand.accentGold}40` }}
            >
              <AlertCircle className="size-3.5 shrink-0 mt-0.5" style={{ color: brand.accentGold }} />
              <span>
                ¡Genial! Si puedes, también coloca un pin en el mapa para que el repartidor llegue más rápido.
              </span>
            </div>
          )}

          {/* Notes */}
          <FormField label="Referencia / notas (opcional)">
            <textarea
              value={form.notes}
              onChange={e => set("notes", e.target.value)}
              placeholder="Ej: Casa azul, segundo piso, timbre roto..."
              rows={2}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-slate-400 resize-none"
            />
          </FormField>

          {/* Global submit error */}
          {errors.submit && (
            <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5">
              <AlertCircle className="size-4 text-red-500 shrink-0 mt-0.5" />
              <p className="text-xs text-red-600">{errors.submit}</p>
            </div>
          )}
        </div>

        {/* Sticky submit button */}
        <div className="shrink-0 px-4 py-4 border-t border-slate-100 bg-white">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center justify-center gap-2 w-full rounded-2xl py-4 text-base font-extrabold text-white shadow-sm transition-all active:scale-95 disabled:opacity-60"
            style={{ background: brand.primary }}
          >
            {submitting ? (
              <>
                <span className="size-5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                Procesando pedido...
              </>
            ) : (
              <>
                <ShoppingBag className="size-5" />
                Confirmar pedido
                <ChevronRight className="size-4" />
              </>
            )}
          </button>
          <p className="text-[11px] text-slate-400 text-center mt-2">
            Pagas al recibir tu producto. Sin cargos por adelantado.
          </p>
        </div>
      </div>
    </>
  );
}

// ── Form field wrapper ────────────────────────────────────────────────────────

function FormField({ label, error, required, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {hint && (
        <p className="text-xs text-slate-400 mb-2 leading-relaxed">{hint}</p>
      )}
      {children}
      {error && <p className="mt-1 text-xs text-red-500 font-medium">{error}</p>}
    </div>
  );
}
